import { RefObject, useEffect, useState } from "react";

import { setSoremonPanelOpen } from "./soremonStore";

/**
 * soremon パネルの表示状態。
 *
 * - 初めて開いたときにマウントし、以後は閉じてもマウントしたままにする（入力途中の
 *   文章やスクロール位置を保つ）。
 * - `shown` は開いた 1 フレーム後に true にし、閉じた状態から開くアニメーションを効かせる。
 * - 開いている間は、外側クリック（アプリ本体 #root の中。body 直下に出るモーダル・
 *   ツールチップ・トーストは除く）と Esc で閉じる。開閉ボタン（data-soremon-trigger）
 *   のクリックは外側として扱わない。
 */
export const useSoremonPanelVisibility = (
  open: boolean,
  panelRef: RefObject<HTMLElement | null>,
): { mounted: boolean; shown: boolean } => {
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!open) {
      setShown(false);
      return;
    }
    setMounted(true);
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setShown(true));
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target || !document.getElementById("root")?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      if (target.closest("[data-soremon-trigger]")) return;
      setSoremonPanelOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSoremonPanelOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, panelRef]);

  return { mounted, shown };
};
